import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, ArrowRight, Loader2, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_BG = "https://images.pexels.com/photos/302769/pexels-photo-302769.jpeg?auto=compress&cs=tinysrgb&w=1920";
const IMG_SUPPORT = "https://images.unsplash.com/photo-1766066014237-00645c74e9c6?w=600&q=80";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }),
};

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (key, val) => setForm({ ...form, [key]: val });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Enquiry submitted successfully!");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero with background */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0B203B]/90" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="max-w-2xl" initial="hidden" animate="visible" variants={fadeUp}>
            <span className="text-sm font-semibold tracking-wider uppercase text-[#FF6600]">Contact Us</span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mt-2 mb-4">
              Get in Touch
            </h1>
            <p className="text-lg text-gray-300 leading-relaxed">
              Have a question? Want a personalised comparison? Our Sydney-based team is here to help.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <motion.div className="space-y-8" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                {/* Team image */}
                <img src={IMG_SUPPORT} alt="Our support team" className="rounded-2xl w-full h-48 object-cover shadow-lg" />

                <div>
                  <h3 className="font-bold text-[#0B203B] text-xl mb-6">Contact Information</h3>
                  <div className="space-y-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-[#FF6600]/10 flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-[#FF6600]" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#0B203B] text-sm">Phone</p>
                        <a href="tel:1300796797" className="text-[#64748B] hover:text-[#FF6600] transition-colors">1300 796 797</a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-[#FF6600]/10 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-[#FF6600]" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#0B203B] text-sm">Email</p>
                        <a href="mailto:hello@salescorpaustralia.com.au" className="text-[#64748B] hover:text-[#FF6600] transition-colors text-sm break-all">hello@salescorpaustralia.com.au</a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-[#FF6600]/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-[#FF6600]" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#0B203B] text-sm">Location</p>
                        <p className="text-[#64748B] text-sm">Sydney, NSW, Australia</p>
                        <p className="text-xs text-[#64748B] mt-1">Servicing all of Australia</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-[#FF6600]/10 flex items-center justify-center flex-shrink-0">
                        <Clock className="w-5 h-5 text-[#FF6600]" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#0B203B] text-sm">Business Hours</p>
                        <p className="text-[#64748B] text-sm">Mon - Fri: 9am - 6pm AEST</p>
                        <p className="text-[#64748B] text-sm">Sat: 9am - 1pm AEST</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-[#F8FAFC] rounded-2xl border border-[#E2E8F0] p-6">
                  <h4 className="font-bold text-[#0B203B] mb-3">Quick Comparison</h4>
                  <p className="text-sm text-[#64748B] mb-4">Want to get started right away? Use our online comparison tool.</p>
                  <Link to="/compare">
                    <Button className="w-full bg-[#FF6600] hover:bg-[#E55C00] text-white font-bold rounded-full" data-testid="contact-compare-btn">
                      Compare Now <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1}>
                {submitted ? (
                  <div className="bg-white rounded-2xl border border-[#E2E8F0] p-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-4">
                      <Send className="w-8 h-8 text-[#10B981]" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#0B203B] mb-2">Thank You!</h3>
                    <p className="text-[#64748B] mb-6">Your enquiry has been received. Our team will get back to you within 24 hours.</p>
                    <Button onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", subject: "", message: "" }); }} variant="outline" data-testid="contact-send-another">
                      Send Another Enquiry
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-[#E2E8F0] p-6 md:p-8" data-testid="contact-form">
                    <h3 className="font-bold text-[#0B203B] text-xl mb-6">Send Us an Enquiry</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-semibold text-[#2E2E33] mb-1.5">Full Name *</label>
                        <Input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="John Smith" className="h-12 rounded-xl" required data-testid="contact-name" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[#2E2E33] mb-1.5">Email *</label>
                        <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="john@example.com" className="h-12 rounded-xl" required data-testid="contact-email" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[#2E2E33] mb-1.5">Phone</label>
                        <Input type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="04XX XXX XXX" className="h-12 rounded-xl" data-testid="contact-phone" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[#2E2E33] mb-1.5">Subject</label>
                        <Select onValueChange={(val) => update("subject", val)}>
                          <SelectTrigger className="h-12 rounded-xl" data-testid="contact-subject">
                            <SelectValue placeholder="Select subject" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general">General Enquiry</SelectItem>
                            <SelectItem value="comparison">Request a Comparison</SelectItem>
                            <SelectItem value="business">Business Enquiry</SelectItem>
                            <SelectItem value="partnership">Partnership</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="mb-6">
                      <label className="block text-sm font-semibold text-[#2E2E33] mb-1.5">Message *</label>
                      <textarea
                        value={form.message}
                        onChange={(e) => update("message", e.target.value)}
                        placeholder="How can we help you?"
                        className="w-full h-32 rounded-xl border border-[#E2E8F0] p-3 text-sm focus:ring-2 focus:ring-[#FF6600] focus:border-[#FF6600] outline-none resize-none"
                        required
                        data-testid="contact-message"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="bg-[#FF6600] hover:bg-[#E55C00] text-white font-bold rounded-full px-8 py-3"
                      data-testid="contact-submit-btn"
                    >
                      {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : <>Send Enquiry <ArrowRight className="w-4 h-4 ml-2" /></>}
                    </Button>
                  </form>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
